import React from 'react';
import { motion } from 'framer-motion';
import { ArrowDown, ArrowRight } from 'lucide-react';
import personalData from '../../data/personal';
import projects from '../../data/projects';
import ProductShowcase from './ProductShowcase';

const proofPoints = [
  { value: '6+', label: 'Years shipping software' },
  { value: String(projects.length), label: 'Selected systems' },
  { value: 'End-to-end', label: 'Product, UX, and engineering' },
];

const Hero = () => {
  return (
    <section id="home" className="relative overflow-hidden bg-[#fbfcff] pt-[76px] sm:pt-[84px]">
      <div className="hero-grid" aria-hidden="true" />
      <div className="hero-layout container-wrapper relative z-10 grid items-center gap-10 pb-14 pt-12 sm:pb-16 sm:pt-14 lg:gap-12 lg:py-14 xl:min-h-[calc(100svh-84px)] xl:grid-cols-[0.9fr_1.1fr] xl:gap-16">
        <div className="hero-copy max-w-[650px]">
          <motion.div
            className="hero-eyebrow mb-6 inline-flex items-center gap-2.5 rounded-full border border-slate-200 bg-white px-4 py-2 text-[10px] font-extrabold uppercase tracking-[0.13em] text-slate-600 shadow-sm sm:mb-8 sm:text-[11px]"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease: [0.19, 1, 0.22, 1] }}
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-50 motion-reduce:animate-none" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
            </span>
            Josh Macaraig / Product engineer
          </motion.div>

          <motion.h1
            className="hero-title max-w-3xl text-[43px] font-black leading-[0.94] tracking-[-0.045em] text-[#17171d] sm:text-6xl lg:text-[68px] xl:text-[78px]"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.75, delay: 0.08, ease: [0.19, 1, 0.22, 1] }}
          >
            I build the systems your team runs on.
          </motion.h1>

          <motion.p
            className="hero-summary mt-6 max-w-xl text-[15px] leading-7 text-slate-600 sm:mt-7 sm:text-lg sm:leading-8"
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2, ease: [0.19, 1, 0.22, 1] }}
          >
            I turn operational complexity into dashboards, AI workflows, internal tools, and
            launch-ready products, from the first workflow map to production.
          </motion.p>

          <motion.div
            className="hero-actions mt-8 flex flex-col gap-3 sm:mt-9 sm:flex-row"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65, delay: 0.32, ease: [0.19, 1, 0.22, 1] }}
          >
            <a
              href={personalData.calendly}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary group min-h-[48px]"
            >
              Book a 1:1 call
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </a>
            <a href="#work" className="btn-secondary group min-h-[48px]">
              View selected work
              <ArrowDown className="h-4 w-4 transition-transform group-hover:translate-y-0.5" />
            </a>
          </motion.div>

          <motion.dl
            className="hero-proof mt-9 grid grid-cols-3 border-t border-slate-200 pt-5 sm:mt-11 sm:pt-6"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65, delay: 0.42, ease: [0.19, 1, 0.22, 1] }}
          >
            {proofPoints.map((proof, index) => (
              <div key={proof.label} className={`${index > 0 ? 'border-l border-slate-200 pl-3 sm:pl-5' : 'pr-3 sm:pr-5'}`}>
                <dt className="text-[10px] font-bold uppercase leading-4 tracking-[0.08em] text-slate-500">
                  {proof.label}
                </dt>
                <dd className="mt-1.5 text-sm font-black leading-tight text-slate-900 sm:text-lg">
                  {proof.value}
                </dd>
              </div>
            ))}
          </motion.dl>
        </div>

        <motion.div
          className="relative mx-auto w-full max-w-[680px] lg:mx-0"
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.18, ease: [0.19, 1, 0.22, 1] }}
        >
          <ProductShowcase />
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
